/**
 * Owns the scene ↔ URL coupling for the map.
 *
 * - URL → state: when `sceneDate`/`sceneLayerKey` query params arrive, a
 *   compare-mode link ([earlier, later]) restores both panes via SET_IMAGE_REFS,
 *   then the params are stripped. Single-scene links are handled by
 *   UrlSceneHandler via the returned `imageRefFromUrl`.
 * - state → URL: while the internal Alt+I toggle (see Hotkeys.tsx) has left
 *   scene params in the URL, they're kept pointed at whatever scene(s) are on
 *   the map as the user navigates.
 *
 * Returns the single-scene `imageRefFromUrl` for UrlSceneHandler while a
 * single-scene deep link is present.
 */

import * as I from 'immutable';
import isEqual from 'lodash/isEqual';
import queryString from 'query-string';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {ApiFeatureData} from 'app/modules/Remote/Feature';
import * as layers from 'app/utils/layers';
import * as mapUtils from 'app/utils/mapUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {MapStateDispatch} from '../MapStateProvider';

interface Args {
  featureData: I.ImmutableOf<ApiFeatureData[]> | null;
  imageRefs: mapUtils.MapImageRefs;
  mapStateDispatch: MapStateDispatch;
}

export default function useSceneUrlState({
  featureData,
  imageRefs,
  mapStateDispatch,
}: Args): mapUtils.MapImageRef | null {
  const location = useLocation();
  const navigate = useNavigate();

  // Alt+I stamps the navigation it makes with the flag, but navigations it
  // doesn't make (see useChangeSelection) carry no state at all, so the last
  // stamp we saw stands until the toggle stamps a new one. Reading it straight
  // off `location.state` would turn the mirror off the next time the user
  // clicks a property, leaving the stale params to be consumed as a deep link.
  const mirroringStamp = location.state?.[routeUtils.SCENE_URL_MIRRORING_STATE_KEY];
  const [wasMirroringSceneInUrl, setWasMirroringSceneInUrl] = React.useState(
    mirroringStamp === true
  );
  const isMirroringSceneInUrl =
    typeof mirroringStamp === 'boolean' ? mirroringStamp : wasMirroringSceneInUrl;

  React.useEffect(() => {
    if (typeof mirroringStamp === 'boolean') {
      setWasMirroringSceneInUrl(mirroringStamp);
    }
  }, [mirroringStamp]);

  const queryParams = React.useMemo(() => queryString.parse(location.search), [location.search]);
  const sceneDateFromQueryParams = routeUtils.getStringParamFromQueryParams(
    queryParams,
    routeUtils.SCENE_DATE_QUERY_PARAM
  );
  const sceneLayerKeyFromQueryParams = routeUtils.getStringParamFromQueryParams(
    queryParams,
    routeUtils.SCENE_LAYER_KEY_QUERY_PARAM
  );

  // In compare mode the scene params are multi-valued ([earlier, later]); the
  // second value, when present, is the compare pane. (Written by Hotkeys.tsx.)
  const sceneDateValues = queryParams[routeUtils.SCENE_DATE_QUERY_PARAM];
  const sceneLayerKeyValues = queryParams[routeUtils.SCENE_LAYER_KEY_QUERY_PARAM];
  const compareSceneDateFromQueryParams = Array.isArray(sceneDateValues)
    ? (sceneDateValues[1] ?? undefined)
    : undefined;
  const compareSceneLayerKeyFromQueryParams = Array.isArray(sceneLayerKeyValues)
    ? (sceneLayerKeyValues[1] ?? undefined)
    : undefined;

  // Navigate to the same route with a new query string, preserving the path and
  // hash. Used for both stripping and mirroring the scene params below.
  const replaceSearch = React.useCallback(
    (nextParams: queryString.ParsedQuery<string>) => {
      const search = queryString.stringify(nextParams);
      navigate(`${location.pathname}${search ? `?${search}` : ''}${location.hash}`, {
        replace: true,
        state: location.state,
      });
    },
    [location.pathname, location.hash, location.state, navigate]
  );

  const clearSceneParams = React.useCallback(() => {
    const newQueryParams = {...queryParams};
    routeUtils.ORDERABLE_SCENE_URL_PARAMS.forEach((param) => {
      delete newQueryParams[param];
    });
    replaceSearch(newQueryParams);
  }, [queryParams, replaceSearch]);

  React.useEffect(() => {
    // Wait for feature data so UrlSceneHandler has a chance to consume a
    // single-scene link before its params are stripped. Mirrored params belong
    // to the internal URL-sharing shortcut and are not navigation requests.
    if (!featureData || !sceneDateFromQueryParams || isMirroringSceneInUrl) {
      return;
    }
    if (compareSceneDateFromQueryParams) {
      // Compare-mode deep link: restore both panes. A 2-element imageRefs array
      // puts the map back into COMPARE mode. Single-scene links are handled by
      // UrlSceneHandler via imageRefFromUrl.
      mapStateDispatch({
        type: 'SET_IMAGE_REFS',
        imageRefs: [
          {
            cursor: sceneDateFromQueryParams,
            layerKey: sceneLayerKeyFromQueryParams ?? layers.ANY_TRUECOLOR_HIGH_RES,
          },
          {
            cursor: compareSceneDateFromQueryParams,
            layerKey: compareSceneLayerKeyFromQueryParams ?? layers.ANY_TRUECOLOR_HIGH_RES,
          },
        ] as mapUtils.MapImageRefs,
      });
    }
    clearSceneParams();
  }, [
    featureData,
    sceneDateFromQueryParams,
    sceneLayerKeyFromQueryParams,
    compareSceneDateFromQueryParams,
    compareSceneLayerKeyFromQueryParams,
    mapStateDispatch,
    clearSceneParams,
    isMirroringSceneInUrl,
  ]);

  const currentPrimaryCursor = imageRefs[0]?.cursor ?? null;
  const currentPrimaryLayerKey = imageRefs[0]?.layerKey ?? null;
  const currentCompareCursor = imageRefs[1]?.cursor ?? null;
  const currentCompareLayerKey = imageRefs[1]?.layerKey ?? null;

  React.useEffect(() => {
    // While the scene link is "on" (the internal Alt+I toggle has put scene
    // params in the URL), keep it pointed at whatever scene(s) are on the map as
    // the user navigates.
    if (!isMirroringSceneInUrl || !sceneDateFromQueryParams || !currentPrimaryCursor) {
      return;
    }
    const desiredDate: string | string[] = currentCompareCursor
      ? [currentPrimaryCursor, currentCompareCursor]
      : currentPrimaryCursor;
    const desiredLayerKey: string | string[] =
      currentCompareCursor && currentCompareLayerKey
        ? [currentPrimaryLayerKey ?? layers.ANY_TRUECOLOR_HIGH_RES, currentCompareLayerKey]
        : (currentPrimaryLayerKey ?? layers.ANY_TRUECOLOR_HIGH_RES);

    // Already in sync — bail so we don't loop on our own navigate().
    if (
      isEqual(queryParams[routeUtils.SCENE_DATE_QUERY_PARAM], desiredDate) &&
      isEqual(queryParams[routeUtils.SCENE_LAYER_KEY_QUERY_PARAM], desiredLayerKey)
    ) {
      return;
    }

    replaceSearch({
      ...queryParams,
      [routeUtils.SCENE_DATE_QUERY_PARAM]: desiredDate,
      [routeUtils.SCENE_LAYER_KEY_QUERY_PARAM]: desiredLayerKey,
    });
  }, [
    isMirroringSceneInUrl,
    sceneDateFromQueryParams,
    currentPrimaryCursor,
    currentPrimaryLayerKey,
    currentCompareCursor,
    currentCompareLayerKey,
    queryParams,
    replaceSearch,
  ]);

  // Memoized because UrlSceneHandler applies each link once, by identity. A
  // fresh object each render would reset the map, and so re-render, on every
  // render until the params are stripped, which starves the router transition
  // that strips them.
  return React.useMemo(
    () =>
      !isMirroringSceneInUrl && sceneDateFromQueryParams && !compareSceneDateFromQueryParams
        ? {
            cursor: sceneDateFromQueryParams,
            // Fall back to truecolor for old URLs
            layerKey: sceneLayerKeyFromQueryParams ?? layers.ANY_TRUECOLOR_HIGH_RES,
          }
        : null,
    [
      isMirroringSceneInUrl,
      sceneDateFromQueryParams,
      compareSceneDateFromQueryParams,
      sceneLayerKeyFromQueryParams,
    ]
  );
}
